/**
 * THE REASON CODES, AS THE WIRE SEES THEM.
 *
 * A SECOND DECLARATION OF A LIST THE ENGINE OWNS, ON PURPOSE. The app cannot import the
 * engine — that is what `api/src/engine/` being inside the service is for: an athlete's
 * client must never re-rank or re-filter a suggestion, and being unable to see the engine is
 * the cheapest possible enforcement of that. The engine, in the other direction, cannot
 * import `shared/`: its boundary test bans every non-relative specifier, which is what keeps
 * it liftable. Neither half may name the other, so the list is declared twice and ASSERTED
 * EQUAL IN BOTH DIRECTIONS — the same shape as the engine's own MAY_IMPORT matrix checked
 * against the directories actually on disk. Two guarded declarations beat one unguarded
 * coupling.
 *
 * WHERE EACH DIRECTION IS GUARDED:
 *   · engine → here: `api/tests/engine/engine-contract.int.spec.ts` asserts set equality both
 *     ways, so neither a new engine code nor a stale entry here can hide.
 *   · here → copy: `src/app/i18n/__tests__/engine-reasons.test.ts` (BUD-277) asserts every
 *     code below has wording in `en` AND `de`, and that no wording exists for a code that is
 *     not below. Two separate assertions, so the failure names the fix: one says "write the
 *     sentence", the other says "delete the stale string".
 *
 * WHERE THE WORDING LIVES, AND WHAT KEYS IT. `src/app/locales/<lang>/common.json`, under
 * `reasons.*`, KEYED BY THE CODE ITSELF. The codes are dotted and i18next splits keys on
 * dots, so a code's own structure IS the file's nesting and nothing transforms anything at
 * render time.
 *
 * THE ENGINE'S `EXPLANATION_KEYS` IS DELIBERATELY UNUSED BY THE APP, AND MUST STAY THAT WAY.
 * `api/src/engine/explanation/index.ts` maps every code onto a camelCase message key
 * (`buddy.engineReasons.conditioningDoseFromTheShape`) — v1's key scheme, carried in with the
 * vendored tree. Wiring it up would mean publishing and syncing a SECOND identifier map
 * across this same boundary, for no gain: a code is already a stable identifier, which is the
 * entire point of codes-rather-than-sentences. Rewriting it instead would mean editing a
 * vendored tree that is REPLACED rather than edited in place. So it is neither used nor
 * changed, and this paragraph is here so nobody later wires it up believing it is required.
 *
 * It is not a test artefact. A `Reason` crosses the network — it is what a session card shows
 * when it explains itself — so this list has to exist on the app's side whatever the tests do.
 *
 * The order and the section comments are the engine's, kept verbatim so the two files diff
 * line for line. Generated from `api/src/engine/contracts/index.ts` rather than retyped.
 */

export const REASON_CODES = [
  /* ── WHY A PRESCRIPTION IS THE SIZE IT IS ── */
  "conditioning.dose-from-the-shape",
  "conditioning.rounds-grown",
  "conditioning.fitted-to-interval",
  "conditioning.station-fitted-to-cap",
  "strength.working-sets-at-this-gear",
  "strength.extra-sets-for-a-short-session",
  "strength.giant-set-is-shorter-and-lighter",
  "strength.complex-limited-by-weakest-link",
  "strength.build-to-a-heavy-top-set",
  "strength.heavy-working-weight",
  "strength.gear-runs-no-max-effort",
  "ramp.last-step-sits-under-the-working-weight",
  "maxset.establishes",
  "maxset.retests",
  "maxset.gear-runs-no-max-effort",
  "maxset.strength-is-already-a-test",
  "maxset.conditioning-is-already-long",
  "maxset.nothing-safe-to-test",
  "cap.from-expected-work",
  "cap.fixed",
  "cap.self-terminating",
  "accessory.sized-to-the-room-it-was-given",
  /* ── WORK ANSWERED ON DEMAND, FOR A METCON ALREADY DRAWN (BUD-335). A drawn session is
       metcon-only; these are what the sheet says about the three offers it comes back with,
       and about the times it comes back with fewer or none. ── */
  "offer.leaves-the-metcon-alone",
  "offer.same-kit-the-metcon-needs",
  "offer.nothing-rests-after-this-metcon",
  "offer.this-room-has-no-strength-kit",
  "offer.heavy-days-this-week-are-spent",
  "offer.today-is-already-a-heavy-day",
  "offer.the-metcon-has-already-started",
  "offer.the-warm-up-did-not-cover-this",
  "offer.repeats-a-metcon-pattern",
  "maxset.added-work-is-not-a-measurement",
  /* ── THE FULLER LIST OF LIFTS BEHIND `Browse` (BUD-338). The curated sheet above chooses
       FOR the athlete; this list is them choosing, so the coaching review split the rested-lift
       rule where the sheet does not: a repeated movement PATTERN is refused and never rendered
       (no member can see that a swing and a deadlift are the same movement), while shared
       TISSUE is shown, badged and sorted last (a metcon that feels harder is their call). The
       three strengths are a property of the CODE and not of the client: `already-in-todays-
       metcon` is informational, `same-joints` and `you-have-worked-this-already` are
       cautionary, and `todays-workout-is-built-on-this` names rows nobody is being shown. ── */
  "browse.already-in-todays-metcon",
  "browse.same-joints-as-the-metcon",
  "browse.you-have-worked-this-already",
  "browse.todays-workout-is-built-on-this",
  "browse.no-lift-in-this-pattern",
  "browse.above-the-athletes-rung",
  "browse.barbell-needs-a-recorded-max",
  "browse.list-cut-to-a-page",

  /* ── WHAT ELSE COULD GO ON THIS LINE (BUD-336). A substitution holds the reps and the place
       in the round fixed and re-solves the load; these are what one substitute says about
       itself, and what the list says when it comes back empty. ── */
  "swap.same-reps-same-place",
  "swap.same-kit-the-line-already-needs",
  "swap.closest-to-the-original-work",
  "swap.a-rung-below",
  "swap.nothing-else-fits-this-line",
  /* A weight the athlete asked for that this room cannot build for that movement — the line
     editor's stepper moves along a published ladder, so this fires on a stale one (BUD-347). */
  "swap.that-weight-is-not-one-this-gym-builds",

  "warmup.no-raiser-in-this-room",
  "warmup.raise-is-a-jumping-drill",
  "warmup.raise-is-continuous",
  "cooldown.flush-then-stretch",

  /* ── WHY A COLUMN WAS DECLINED. A refusal is a RESULT, and these are the results. ── */
  "gear.unknown",
  "shape.not-offered-at-this-intensity",
  "format.not-offered-at-this-intensity",
  "conditioning.no-dose-fits-this-interval",
  "library.no-shape-fits-this-room",
  "request.minutes-available-too-short",

  /* ── WHAT THE DRAW AND THE COACH REJECTED. Diagnostics: the signal that shows where the
       shape library is thin, and the one place a ban is visible after the fact. ── */
  "library.shape-cannot-be-filled-in-this-room",
  "coach.movement-excluded",
  "coach.shape-excluded",
  "coach.movement-preferred",
  "coach.shape-preferred",

  /* ── WHAT THE ATHLETE'S OWN WISHES DID (BUD-334). An athlete's AVOID has no code here: it
       rides the restriction channel, so the `restriction.*` codes report it. ── */
  "wish.movement-wanted",
  "wish.movement-not-in-this-room",
  "wish.movement-above-your-rung",
  "wish.shapes-narrowed",
  "wish.left-nothing-buildable",
  "wish.wants-left-nothing-buildable",

  /* ── WHAT THE CALLER OPTED INTO, WHERE IT ACTUALLY FIRED ── */
  "relaxation.coach-exclusions-dropped",
  "relaxation.minutes-available-ignored",

  /* ── WHAT THE REQUEST ITSELF DECIDED ── */
  "request.minutes-available-sets-the-target",
  "request.input-not-considered",

  /* ── WHAT WAS SUBSTITUTED FOR AN INPUT THAT WAS NEVER GIVEN (BUD-255). The engine works
       across the whole range of how much it knows, so "nothing" is a normal request — and the
       receipt has to say what stood in for what, or the athlete is priced against a population
       model with nothing anywhere admitting it. ── */
  "athlete.population-floor-assumed",
  "room.floor-assumed",
  "room.unlisted-kit-assumed-present",
  "history.sourced-the-draw",
  "strength.no-room-in-the-time-given",

  /* ── WHAT A RESTRICTION DID (BUD-255). A restriction is an input like any other and it obeys
       the same rule: it either changed the output and says how, or it says it changed nothing. ── */
  "restriction.movements-removed",
  "restriction.load-capped",
  "restriction.no-effect-on-this-session",
  "restriction.expired",
  "restriction.left-nothing-buildable",

  /* ── WHY A SHAPE CANNOT BE RUN TODAY (BUD-333). The shape picker's cause breakdown, and the
       one place in this list where the SAME refusal is deliberately reported under three
       different codes depending on WHO can change it: a wish the athlete may drop, a fact about
       the room they are standing in, and everything neither of those covers. The client renders
       them differently and offers a control for exactly one of them, so flattening them into one
       list of strings would delete the only distinction the screen is built on. ── */
  "fit.a-wish-rules-out-the-movements",
  "fit.nothing-fits-what-you-asked-for",
  "fit.your-gym-does-not-have-it",
  "fit.your-gym-cannot-load-it",
  "fit.a-restriction-rules-out-the-movements",
  "fit.above-what-you-can-do-today",
  "fit.no-movement-in-the-library-fits-it",

  /* ── WHAT A VERIFICATION GATE FOUND (BUD-253). One session first, then the columns
       compared against each other. A gate emits a reason exactly like every rule above it
       does, and for the same reason: the registry row says WHICH gate fired and how loudly,
       and the code says WHAT it saw. Neither of them holds the sentence. ── */
  "time.over-the-ceiling",
  "time.past-the-time-you-said",
  "time.past-what-a-session-may-ever-run",
  "time.under-the-floor",
  "relaxation.let-this-through",
  "machine.two-in-one-session",
  "machine.disagrees-with-the-pin",
  "barbell.two-loads-in-one-piece",
  "load.the-room-cannot-build-it",
  "movement.above-the-athletes-rung",
  "volume.over-the-ceiling",
  "interval.round-overflows-the-window",
  "restriction.movement-is-restricted",
  "reasons.block-carries-none",
  "requirements.strip-disagrees-with-the-blocks",
  "escalation.less-volume-at-the-harder-gear",
  "escalation.lighter-at-the-harder-gear",
  "escalation.step-adds-nothing",
  "dose.not-a-real-dose",
  "duplicate.this-session-has-been-given-before",
  "offer.every-gear-refused",
] as const;

export type ReasonCode = (typeof REASON_CODES)[number];
